/**
 * お支払い情報変更画面
 * 現在の支払い方法を表示しつつ、変更可能な支払い方法をラジオボタンで選択させる。
 * 初期表示時に規制情報APIを2種類呼び出し、利用不可な決済手段を選択不可として制御する。
 */
import type { NextPage } from 'next';
import { useRecoilState, useRecoilValue, useResetRecoilState } from 'recoil';

import BackLinkButton from '@/components/atoms/BackLinkButton';
import LinkButton from '@/components/atoms/LinkButton';
import RadioButton, { OptionDefinition } from '@/components/atoms/RadioButton';

import { PaymentInfoState, PaymentMethodState } from '@/states/paymentInfoState';
import { PaymentInfoType } from '@/types/api/mypage/paymentInfo';

import Heading from '@/components/atoms/Heading';
import BreadcrumbLists from '@/components/templates/BreadcrumbLists';
import { userState } from '@/states/selectors/userState';
import { Constants } from '@/utils/constants';

import { AnalyticsData } from '@/components/templates/AnalyticsData';
import PaymentTable from '@/components/templates/PaymentTable';
import useApiOption from '@/hooks/api/services/payment/restriction/useApiOption';
import historyContext from '@/hooks/historyContext';
import { serviceIdState } from '@/states/serviceInfoState';
import { Response } from '@/types/api/mypage/services/payment/restriction/option';
import { useRouter } from 'next/router';
import { useContext, useEffect, useState } from 'react';
import {
  isRestrictAccountTransfer,
  isRestrictAu,
  isRestrictCreditCard,
  isRestrictNtt,
  isRestrictSoftbank,
} from '@/logics/payment';
import { JudgeAccountTransferResponse } from '@/types/api/mypage/services/payment/restriction/judgeAccountTransfer';
import useApiJudgeAccountTransfer from '@/hooks/api/services/payment/restriction/useApiJudgeAccountTransfer';

const PaymentInfoChange: NextPage = () => {
  // 選択済みのサービスID
  const serviceId = useRecoilValue(serviceIdState);

  const router = useRouter();
  const paymentInfo = useRecoilValue<PaymentInfoType>(PaymentInfoState);
  // ラジオボタンの選択を保持する
  const [paymentMethod, setPaymentMethod] = useRecoilState<string>(PaymentMethodState);
  // ラジオボタンの選択リセット
  const resetPaymentMethod = useResetRecoilState(PaymentMethodState);
  // 履歴
  const history = useContext(historyContext);
  // ユーザー情報
  const user = useRecoilValue(userState);
  // 「支払情報：規制情報（オプション）」のフック処理
  const { get: getRestrictionOption } = useApiOption();
  // 「支払情報：規制情報（口座振替）」のフック処理
  const { get: getRestrictionJudgeAccountTransfer } = useApiJudgeAccountTransfer();
  // 「支払情報：規制情報（オプション）」の状態保持
  const [restrictionOption, setRestrictionOption] = useState<Response>();
  // 「支払情報：規制情報（口座振替）」の状態保持
  const [restrictionJudgeAccountTransfer, setRestrictionJudgeAccountTransfer] =
    useState<JudgeAccountTransferResponse>();

  /**
   * 表示可能な支払い方法一覧を組み立てる。
   * 規制情報が未取得の間は選択肢を出さず、取得後にのみ無効状態を含めて表示する。
   */
  const availablePaymentMethod: OptionDefinition[] = (() => {
    // 規制情報未取得の場合は、選択肢は表示しない
    const options: OptionDefinition[] = [];
    if (!restrictionOption || !restrictionJudgeAccountTransfer) {
      return options;
    }

    // クレジットカード
    options.push({
      label: `${Constants.PaymentMethod.CREDIT_CARD.name}でのお支払い`,
      value: Constants.PaymentMethod.CREDIT_CARD.code,
      disabled: isRestrictCreditCard({ methods: restrictionOption.methods }),
    });

    // 自動口座振替
    // マンションはUFJのため除く
    if (!user.isMansion) {
      options.push({
        label: `${Constants.PaymentMethod.ACCOUNT_TRANSFER.name}サービスでのお支払い`,
        value: Constants.PaymentMethod.ACCOUNT_TRANSFER.code,
        disabled:
          isRestrictAccountTransfer({ methods: restrictionOption.methods }) ||
          isRestrictAccountTransfer({ methods: restrictionJudgeAccountTransfer.methods }),
      });
    }

    // NTT
    options.push({
      label: `${Constants.PaymentMethod.NTT_TEL.nameShort}でのお支払い`,
      value: Constants.PaymentMethod.NTT_TEL.code,
      disabled: isRestrictNtt({ methods: restrictionOption.methods }),
    });

    // SB
    options.push({
      label: `${Constants.PaymentMethod.SB_TEL.nameShort}でのお支払い`,
      value: Constants.PaymentMethod.SB_TEL.code,
      disabled: isRestrictSoftbank({ methods: restrictionOption.methods }),
    });

    // KDDI
    options.push({
      label: `${Constants.PaymentMethod.AU.nameShort}でのお支払い`,
      value: Constants.PaymentMethod.AU.code,
      disabled: isRestrictAu({ methods: restrictionOption.methods }),
    });

    return options;
  })();

  /** ユーザーが選択した支払い方法をRecoil stateに保存する */
  const setPaymentMethodSelected = (value: string) => {
    setPaymentMethod(value);
  };

  /** 選択された支払い方法に応じて遷移先画面を決定する。 */
  const setTransitionTo = (): string => {
    switch (paymentMethod) {
      case Constants.PaymentMethod.CREDIT_CARD.code: {
        return '/contract/payment/credit_card';
      }
      case Constants.PaymentMethod.ACCOUNT_TRANSFER.code: {
        return '/contract/payment/account_transfer';
      }
      case Constants.PaymentMethod.NTT_TEL.code: {
        return '/contract/payment/docomo';
      }
      case Constants.PaymentMethod.SB_TEL.code: {
        return '/contract/payment/softbank';
      }
      case Constants.PaymentMethod.AU.code: {
        return '/contract/payment/kddi';
      }
      default: {
        return '#';
      }
    }
  };

  /**
   * 初期表示処理を実施する
   */
  useEffect(() => {
    if (history[0] === '/contract/') {
      resetPaymentMethod();
    }

    // 支払い方法変更フローを離れたら選択状態を破棄し、別導線へ影響させない。
    const handleRouteChange = (url: string) => {
      if (/\/contract\/payment\//.test(url)) return;
      resetPaymentMethod();
    };

    // 「支払情報：規制情報（オプション）」の取得処理を実施する
    getRestrictionOption({ serviceId }).then((e) => {
      setRestrictionOption(e.data);
    });

    // 「支払情報：規制情報（口座振替）」の取得処理を実施する
    getRestrictionJudgeAccountTransfer({ serviceId }).then((e) => {
      setRestrictionJudgeAccountTransfer(e.data);
    });

    router.events.on('routeChangeStart', handleRouteChange);

    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * 初期表示時の選択値を決定する。
   * すでに選択済みならその値を優先し、未選択なら現在契約中の支払い方法を初期値として反映する。
   */
  const currentPayType = () => {
    // 何か選択していたら
    if (paymentMethod) return paymentMethod;
    // 選択しておらず、データからカレント取得
    const check = availablePaymentMethod.some(({ value }) => value === paymentInfo.payment_type);
    if (check) {
      setPaymentMethod(paymentInfo.payment_type);
      return paymentInfo.payment_type;
    }
    // ラジオボタンにないデータの場合、デフォルトはカード
    setPaymentMethod(Constants.PaymentMethod.CREDIT_CARD.code);
    return Constants.PaymentMethod.CREDIT_CARD.code;
  };

  /**
   * 注意事項：規制情報を表示するコンポーネント
   * @param paymentType 支払種別
   * @returns 画面コンポーネント
   */
  const componentJudgeAccountTransferAlert = ({
    restriction,
  }: {
    restriction?: Response | JudgeAccountTransferResponse;
  }) => {
    // API が返す制約理由をそのまま可視化し、選択できない理由を利用者に説明する。
    const reasons = restriction?.reasons;
    if (!reasons || reasons.length === 0) {
      return null;
    }
    const rows = reasons.map((e) => (
      <div className='m-text' key={e.title}>
        <div className='text-alert'>
          <div>{e.title}</div>
          <ul className='m-list'>
            {restriction === restrictionOption ? (
              <ul className='m-list'>
                {e.messages.map((message) => {
                  return (
                    <li className='m-list__item' key={message}>
                      {message}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div>
                {e.messages.map((message) => {
                  return (
                    <div key={message}>
                      {/* eslint-disable-next-line react/no-danger */}
                      <span dangerouslySetInnerHTML={{ __html: message }} />
                    </div>
                  );
                })}
              </div>
            )}
          </ul>
        </div>
      </div>
    ));
    return <div>{rows}</div>;
  };

  return (
    <>
      <AnalyticsData />
      <main className='l-main'>
        <Heading as='h1'>お支払い情報の登録・変更</Heading>
        <div className='m-box-02'>
          <div className='m-box-02__inner'>
            <PaymentTable editPage />
            <p className='m-text'>
              クレジットカードの変更、またはクレジットカードへの変更をご希望の場合は、
              「変更する」をクリックしてください。
            </p>
            <div className='m-text'>
              <RadioButton
                options={availablePaymentMethod}
                isMFormItem
                toBeHidden={false}
                setFunction={setPaymentMethodSelected}
                current={currentPayType()}
              />
            </div>
            {/* 注意事項（規制情報：優先度考慮） */}
            {componentJudgeAccountTransferAlert({
              restriction: restrictionOption?.reasons?.length
                ? restrictionOption
                : restrictionJudgeAccountTransfer,
            })}
            {/* 操作 */}
            <div className='m-button col2'>
              <BackLinkButton href='/contract'>メニューへ戻る</BackLinkButton>
              <LinkButton variant='secondary' href={setTransitionTo()}>
                変更する
              </LinkButton>
            </div>
          </div>
        </div>
        <BreadcrumbLists />
      </main>
    </>
  );
};

export default PaymentInfoChange;
